import { useContext, useEffect } from "react";
import { useRouter } from "next/router";
import { ProgressSteps } from "@aof/aofgql/progressSteps";
import { useTranslation } from "../../../next-i18next";
import { useFeatureFlags } from "../../../context/FeatureFlagsProvider";
import { useEnsureAuth0IdOnUser } from "../../../utils/hooks/useEnsureAuth0IdOnUser";
import { useIsDocuSignFeatureEnabled } from "../../../utils/hooks/useIsDocuSignFeatureEnabled";
import { Routes } from "../../../utils/types/routes";
import { ConfigContext } from "../../../utils/types/config";
var auth0QueryParams = [
    "code",
    "message",
    "success",
    "supportForgotPassword",
    "verified_email",
];
export var Redirect = function (_a) {
    var leadID = _a.leadID, progressToSendDown = _a.progressToSendDown;
    var t = useTranslation().t;
    var router = useRouter();
    var config = useContext(ConfigContext);
    var flags = useFeatureFlags().flags;
    var _b = useIsDocuSignFeatureEnabled(leadID), loadingDocuSign = _b.loading, isDocuSignEnabled = _b.isEnabled;
    var fundingCallPage = flags["funding-call-page"] === true;
    var verifiedEmail = router.query.verified_email === "true";
    var shouldRedirectFromRoot = router.pathname === Routes.Root &&
        progressToSendDown === ProgressSteps.StartLoanLearn;
    useEnsureAuth0IdOnUser();
    useEffect(function () {
        var _a;
        if (!shouldRedirectFromRoot || !router.isReady) {
            return;
        }
        var auth0QueryParamsSet = new Set(auth0QueryParams);
        var sanitizedQuery = Object.fromEntries(Object.entries(router.query).filter(function (_a) {
            var key = _a[0];
            return !auth0QueryParamsSet.has(key);
        }));
        if (verifiedEmail) {
            var learningCenterUrl = (_a = config === null || config === void 0 ? void 0 : config.learningCenterUrl) !== null && _a !== void 0 ? _a : process.env.NEXT_PUBLIC_LEARNING_CENTER_URL;
            // If verified_email=true but the learning center URL is missing, fall back
            // to the portal flow so the user doesn't get stuck on this redirect page.
            if (typeof window !== "undefined" && learningCenterUrl) {
                window.location.href = learningCenterUrl;
                return;
            }
            router.push({
                pathname: Routes.MyAccount,
                query: sanitizedQuery,
            });
            return;
        }
        router.push({
            pathname: Routes.MyAccount,
            query: sanitizedQuery,
        });
    }, [
        config === null || config === void 0 ? void 0 : config.learningCenterUrl,
        progressToSendDown,
        router.isReady,
        router.query,
        shouldRedirectFromRoot,
        router,
        verifiedEmail,
        t,
    ]);
    if (shouldRedirectFromRoot) {
        return <div>{t("Redirecting")}</div>;
    }
    if (loadingDocuSign) {
        return <div>{t("Redirecting")}</div>;
    }
    // Cleanup Auth0 redirection query parameters.
    auth0QueryParams.map(function (qp) {
        delete router.query[qp];
    });
    var pushWithQuery = function (path) {
        router.push({
            pathname: path,
            query: router.query,
        });
    };
    var newFlowSwitch = function (currentStep) {
        switch (currentStep) {
            case ProgressSteps.StartLoan:
                pushWithQuery(Routes.BusinessInformation);
                break;
            case ProgressSteps.StartLoanLearn:
                pushWithQuery(Routes.BusinessInformation);
                break;
            case ProgressSteps.BusinessInformation:
                pushWithQuery(Routes.PersonalInformation);
                break;
            case ProgressSteps.PersonalInformation:
                pushWithQuery(Routes.MyFinances);
                break;
            case ProgressSteps.MyFinances:
                pushWithQuery(Routes.ReviewMyApplication);
                break;
            case ProgressSteps.ConnectBankAccount:
                pushWithQuery(Routes.ConnectBankAccount);
                break;
            case ProgressSteps.SubmittedLoanApplication:
                pushWithQuery(Routes.CheckingApplication);
                break;
            case ProgressSteps.GenericError:
                pushWithQuery(Routes.GenericError);
                break;
            case ProgressSteps.Approved:
                pushWithQuery(Routes.PrequalifiedOffers);
                break;
            case ProgressSteps.ApprovedPartner:
                pushWithQuery(Routes.QualifyPartner);
                break;
            case ProgressSteps.Rejected:
                pushWithQuery(Routes.Rejected);
                break;
            case ProgressSteps.SelectedLoanOffer:
                pushWithQuery(Routes.UploadDocuments);
                break;
            case ProgressSteps.UploadDocuments:
                pushWithQuery(Routes.ExistingLoans);
                break;
            case ProgressSteps.ExistingLoans:
            case ProgressSteps.AdditionalDocumentsSubmitted:
            case ProgressSteps.BeneficialOwnersCompleted:
                pushWithQuery(Routes.WeAreReviewing);
                break;
            case ProgressSteps.ExistingLoansCompletedBeneficialOwnerInfoNeeded:
                pushWithQuery(Routes.BeneficialOwners);
                break;
            case ProgressSteps.AdditionalDocumentsRequired:
                pushWithQuery(Routes.WeRequiereAdditionalInfo);
                break;
            case ProgressSteps.DocumentsReviewed:
                pushWithQuery(Routes.SelectAFinalLoanOffer);
                break;
            case ProgressSteps.SelectedFinalLoanOffer:
                if (fundingCallPage) {
                    pushWithQuery(Routes.FundingCall);
                }
                else if (isDocuSignEnabled) {
                    pushWithQuery(Routes.OneLastThing);
                }
                else {
                    pushWithQuery(Routes.ESign);
                }
                break;
            case ProgressSteps.DemographicInformation:
                pushWithQuery(Routes.DocumentSigning);
                break;
            case ProgressSteps.ReviewRequired:
            case ProgressSteps.ReviewRequiredUploadDocumentsBack:
                pushWithQuery(Routes.ReviewRequired);
                break;
            case ProgressSteps.ReviewRequiredUploadDocuments:
                pushWithQuery(Routes.ReviewRequiredUploadDocuments);
                break;
            case ProgressSteps.NoHitError:
            case ProgressSteps.AfterRejected:
            case ProgressSteps.ApplicationError:
            case ProgressSteps.DocumentSigned:
            case ProgressSteps.PrequalifiedError:
                pushWithQuery(Routes.MyAccount);
                break;
            default:
                console.error("ProgressStep", currentStep, "was not routed");
                break;
        }
    };
    if (router.query.email !== undefined &&
        router.query.sfApplicationId !== undefined &&
        router.pathname !== Routes.ClaimLanding &&
        router.pathname !== Routes.SignUpVerifyAccount) {
        pushWithQuery(Routes.ClaimLanding);
    }
    else {
        newFlowSwitch(progressToSendDown);
    }
    return <div>{t("Redirecting")}</div>;
};
//# sourceMappingURL=data:application/json;base64,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